import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { api, errMsg, BACKEND, fileUrl } from "@/lib/api";
import { brl } from "@/lib/fmt";
import { H, Panel, Table, Form, UploadBtn } from "./ui";

const TIPOS = ["Fitoterápico registrado", "Fitoterápico notificado", "Alimento/Chá", "Suplemento alimentar", "Cosmético", "Produto veterinário MAPA", "Acessório"];
const FIELDS = [
  { k: "nome", label: "Nome" }, { k: "slug", label: "Slug" }, { k: "tipo_regulatorio", label: "Categoria regulatória", type: "select", options: TIPOS },
  { k: "registro", label: "Registro / notificação" }, { k: "enquadramento_ok", label: "Enquadrado na RDC 243/2018 e IN 28/2018 (suplementos)", type: "bool" },
  { k: "nome_cientifico", label: "Nome científico" }, { k: "forma", label: "Forma", type: "select", options: ["capsula", "gotas", "cha", "kit"] },
  { k: "categoria", label: "Categoria da loja", type: "select", options: ["suplementos", "fitoterapicos", "chas", "kits"] },
  { k: "necessidades", label: "Necessidades (uma por linha)", type: "lines", rows: 2 }, { k: "descricao_curta", label: "Descrição curta", type: "textarea", wide: true },
  { k: "modo_uso", label: "Modo de uso", type: "textarea" }, { k: "composicao", label: "Composição", type: "textarea" },
  { k: "advertencias", label: "Advertências", type: "textarea" }, { k: "restrito", label: "Produto com restrição (exige ciência no checkout)", type: "bool" },
  { k: "preco", label: "Preço (R$)", type: "number" }, { k: "preco_de", label: "Preço de (R$)", type: "number" }, { k: "custo", label: "Custo (R$)", type: "number" },
  { k: "desconto_2", label: "Desconto 2 frascos (%)", type: "number", hint: "Vazio = padrão da loja" }, { k: "desconto_3", label: "Desconto 3 frascos (%)", type: "number" },
  { k: "dias_uso", label: "Dias de uso por unidade", type: "number" }, { k: "tamanho", label: "Apresentação (ex.: 60 cápsulas)" },
  { k: "peso_g", label: "Peso (g)", type: "number" }, { k: "dimensoes", label: "Medidas" }, { k: "sku", label: "SKU" }, { k: "ean", label: "EAN" },
  { k: "estoque_minimo", label: "Estoque mínimo", type: "number" }, { k: "relacionados", label: "Relacionados (slugs, um por linha)", type: "lines", rows: 2 },
  { k: "imagens", label: "Imagens (URLs, uma por linha)", type: "lines", rows: 3, wide: true },
  { k: "ingredientes", label: "Cards de ingredientes da galeria [{nome, texto}]", type: "json", wide: true },
  { k: "tabela_nutricional", label: "Tabela nutricional {porcao, itens:[[nutriente, qtd, %VD]]}", type: "json", wide: true },
  { k: "itens_kit", label: "Itens do kit [{slug, quantidade}]", type: "json", wide: true }, { k: "seo", label: "SEO {titulo, descricao}", type: "json", wide: true },
  { k: "ativo", label: "Mostrar na loja (ativo)", type: "bool" },
];

function Editor({ p, setP, claims, defaults }) {
  const d2 = p.desconto_2 ?? defaults.desconto_2, d3 = p.desconto_3 ?? defaults.desconto_3;
  const toggle = (c) => setP({ ...p, alegacoes: p.alegacoes?.includes(c) ? p.alegacoes.filter((x) => x !== c) : [...(p.alegacoes || []), c] });
  return (
    <div className="space-y-6">
      <Form fields={FIELDS} data={p} setData={setP} />
      <UploadBtn label="Enviar foto" accept="image/*" onDone={(u) => setP({ ...p, imagens: [...(p.imagens || []), u] })} />
      <div className="flex gap-2">{(p.imagens || []).map((i) => <img key={i} src={fileUrl(i)} alt="" className="h-16 w-16 rounded-xl border object-contain" />)}</div>
      <div><p className="label-na">Alegações (somente da lista permitida)</p>
        <div className="max-h-56 space-y-1 overflow-y-auto rounded-2xl border border-na-line bg-white p-3" data-testid="claims-picker">
          {claims.map((c) => <label key={c.id} className="flex gap-2 text-sm"><input type="checkbox" checked={p.alegacoes?.includes(c.frase) || false} onChange={() => toggle(c.frase)} />{c.frase} <span className="text-xs text-na-ink2">({c.fonte})</span></label>)}
        </div></div>
      {p.preco > 0 && (
        <div className="rounded-2xl bg-na-sage2 p-4 text-sm" data-testid="price-preview">
          <p className="label-na">Prévia de preços</p>
          <p>1 frasco: {brl(p.preco)} · 2 frascos: {brl(p.preco * 2 * (1 - d2 / 100))} ({d2}% off) · 3 frascos: {brl(p.preco * 3 * (1 - d3 / 100))} ({d3}% off)</p>
          {p.custo > 0 && <p>Margem: {(((p.preco - p.custo) / p.preco) * 100).toFixed(1)}%</p>}
        </div>
      )}
    </div>
  );
}

export default function Products() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [claims, setClaims] = useState([]);
  const [cfg, setCfg] = useState({ desconto_2: 10, desconto_3: 20 });
  const [bulk, setBulk] = useState(false);
  const load = () => api.get("/admin/products").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/admin/allowed_claims").then((r) => setClaims(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setCfg(r.data));
  }, []);
  const save = async () => {
    try {
      if (edit.id) await api.put(`/admin/products/${edit.id}`, edit); else await api.post("/admin/products", edit);
      toast.success("Produto salvo");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e), { duration: 8000 }); }
  };
  const importCsv = async (e) => {
    const fd = new FormData();
    fd.append("file", e.target.files[0]);
    try { const r = await api.post("/admin/products-import", fd); toast.success(`${r.data.importados} importados, ${r.data.erros.length} erros`); load(); } catch (er) { toast.error(errMsg(er)); }
  };
  const applyBulk = async () => { const r = await api.post("/admin/products/bulk-discount", { desconto_2: cfg.desconto_2, desconto_3: cfg.desconto_3 }); toast.success(`${r.data.atualizados} produtos atualizados`); setBulk(false); load(); };
  return (
    <div data-testid="admin-products">
      <H actions={<>
        <a className="btn-line px-4 py-2" href={`${BACKEND}/api/admin/products-export.csv`} data-testid="export-csv"><Download className="h-4 w-4" /> Exportar CSV</a>
        <label className="btn-line cursor-pointer px-4 py-2"><Upload className="h-4 w-4" /> Importar CSV<input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="import-csv" /></label>
        <button className="btn-line px-4 py-2" onClick={() => setBulk(true)} data-testid="bulk-discount-button">Descontos em massa</button>
        <button className="btn-na px-4 py-2" onClick={() => setEdit({ ativo: false, necessidades: [], alegacoes: [], imagens: [], itens_kit: [], ingredientes: [], seo: {}, dias_uso: 30, estoque_minimo: 10 })} data-testid="new-product">Novo produto</button>
      </>}>Produtos</H>
      <Table rows={rows} onRow={(r) => setEdit({ ...r })} testid="products-table" cols={[
        ["img", "", (r) => <img src={fileUrl(r.imagens?.[0])} alt="" className="h-10 w-10 object-contain" />], ["nome", "Nome", (r) => <b>{r.nome}</b>], ["tipo_regulatorio", "Tipo"],
        ["preco", "Preço", (r) => brl(r.preco)], ["margem", "Margem", (r) => (r.margem != null ? `${r.margem}%` : "-")], ["estoque", "Estoque"],
        ["ativo", "Loja", (r) => (r.ativo ? "Ativo" : "Oculto")],
      ]} />
      <Panel open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? edit.nome : "Novo produto"} wide>
        {edit && <><Editor p={edit} setP={setEdit} claims={claims} defaults={cfg} />
          <p className="mt-4 text-xs text-na-ink2">O verificador de termos bloqueia o salvamento se houver termo proibido.</p>
          <button className="btn-na mt-4" onClick={save} data-testid="save-product">Salvar produto</button></>}
      </Panel>
      <Panel open={bulk} onClose={() => setBulk(false)} title="Descontos em massa">
        <div className="grid grid-cols-2 gap-3">
          <div><label htmlFor="b2" className="label-na">2 frascos (%)</label><input id="b2" type="number" className="input-na" value={cfg.desconto_2} onChange={(e) => setCfg({ ...cfg, desconto_2: Number(e.target.value) })} data-testid="bulk-d2" /></div>
          <div><label htmlFor="b3" className="label-na">3 frascos (%)</label><input id="b3" type="number" className="input-na" value={cfg.desconto_3} onChange={(e) => setCfg({ ...cfg, desconto_3: Number(e.target.value) })} data-testid="bulk-d3" /></div>
        </div>
        <button className="btn-na mt-4" onClick={applyBulk} data-testid="bulk-apply">Aplicar a todos os produtos</button>
      </Panel>
    </div>
  );
}
